import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_PROVEEDORES_URL } from '../../constants';
import { Form } from 'react-bootstrap';
import Moment from 'moment';
import 'moment/locale/es';
import SchoolIcon from '@material-ui/icons/School';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import ComputerIcon from '@material-ui/icons/Computer';
import ScheduleIcon from '@material-ui/icons/Schedule';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { Doughnut } from 'react-chartjs-2';
import { esHixsa } from '../../constantes/general-require';

Moment.locale('es');

// Mapeo de valores de desempeño a numéricos
const VALOR_MAP = { 'Excelente': 4, 'Bueno': 3, 'Regular': 2, 'Malo': 1 };

// IDs de las preguntas de desempeño con ícono Material UI
const CRITERIOS_DESEMPENO = [
  { id: 'd1', nombre: 'Conocimiento del personal o soporte', icon: SchoolIcon },
  { id: 'd2', nombre: 'Atención del personal', icon: HeadsetMicIcon },
  { id: 'd3', nombre: 'Tecnología utilizada por proveedor', icon: ComputerIcon },
  { id: 'd4', nombre: 'Tiempos de entrega', icon: ScheduleIcon },
  { id: 'd5', nombre: 'Precio del producto/servicio', icon: LocalOfferIcon },
  { id: 'd6', nombre: 'Calidad del producto o servicio', icon: VerifiedUserIcon }
];

function EvaluacionProveedores({ empresas }) {
  const [datos, setDatos] = useState(null);
  const [promedioGeneral, setPromedioGeneral] = useState(0);
  const [totalEncuestas, setTotalEncuestas] = useState(0);
  const [mesSeleccionado, setMesSeleccionado] = useState(Moment().format('YYYY-MM'));
  const [cargando, setCargando] = useState(false);
  const [sinDatos, setSinDatos] = useState(false);

  const empresaActual = empresas && empresas[0];

  useEffect(() => {
    if (esHixsa(empresaActual)) {
      getEstadisticas();
    }
  }, [mesSeleccionado, empresas]);

  function getEstadisticas() {
    setCargando(true);
    setSinDatos(false);

    const fechaInicio = Moment(mesSeleccionado, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
    const fechaFin = Moment(mesSeleccionado, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');

    axios.post(API_PROVEEDORES_URL + 'encuesta/estadisticas', {
      empresas: empresas,
      fechaInicio: fechaInicio,
      fechaFin: fechaFin
    }).then((res) => {
      if (res.data.result.code === 200 && res.data.respuestas && res.data.respuestas.length > 0) {
        calcularPromedios(res.data.respuestas, res.data.totalEncuestas);
      } else {
        setSinDatos(true);
        setDatos(null);
        setPromedioGeneral(0);
        setTotalEncuestas(0);
      }
    }).catch(() => {
      setSinDatos(true);
      setDatos(null);
    }).finally(() => {
      setCargando(false);
    });
  }

  function calcularPromedios(respuestasRaw, total) {
    const sumas = {};
    const conteos = {};

    CRITERIOS_DESEMPENO.forEach(c => {
      sumas[c.id] = 0;
      conteos[c.id] = 0;
    });

    respuestasRaw.forEach(jsonStr => {
      try {
        const resp = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
        CRITERIOS_DESEMPENO.forEach(c => {
          const valor = resp[c.id];
          if (valor && VALOR_MAP[valor] !== undefined) {
            sumas[c.id] += VALOR_MAP[valor];
            conteos[c.id]++;
          }
        });
      } catch (e) { /* ignorar respuestas mal formadas */ }
    });

    const datosCalculados = CRITERIOS_DESEMPENO.map(c => {
      const promedio = conteos[c.id] > 0 ? sumas[c.id] / conteos[c.id] : 0;
      return {
        ...c,
        promedio: Math.round(promedio * 100) / 100
      };
    });

    const sumaPromedios = datosCalculados.reduce((acc, d) => acc + d.promedio, 0);
    const criteriosConDatos = datosCalculados.filter(d => d.promedio > 0).length;
    const general = criteriosConDatos > 0 ? sumaPromedios / criteriosConDatos : 0;

    setDatos(datosCalculados);
    setPromedioGeneral(Math.round(general * 100) / 100);
    setTotalEncuestas(total);
    setSinDatos(false);
  }

  function getColor(promedio) {
    if (promedio >= 3.5) return '#28a745';
    if (promedio >= 2.5) return '#17a2b8';
    if (promedio >= 1.5) return '#ffc107';
    if (promedio > 0) return '#dc3545';
    return '#ccc';
  }

  function getDonaPromedioData() {
    const color = getColor(promedioGeneral);
    return {
      datasets: [
        {
          data: [promedioGeneral, Math.max(4 - promedioGeneral, 0)],
          backgroundColor: [color, '#eee'],
          hoverBackgroundColor: [color, '#eee'],
          borderWidth: 0
        }
      ]
    };
  }

  function getMesesDisponibles() {
    const meses = [];
    for (let i = 0; i < 12; i++) {
      const mes = Moment().subtract(i, 'months');
      const label = mes.format('MMMM YYYY');
      meses.push({ value: mes.format('YYYY-MM'), label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return meses;
  }

  if (!esHixsa(empresaActual)) {
    return null;
  }

  return (
    <div className="evaluacion-proveedores-container">
      {/* Título principal */}
      <div className="evaluacion-proveedores-tit">Evaluación de proveedores</div>

      {/* Contenedor principal con dos columnas */}
      <div className="evaluacion-contenido">
        <div className="evaluacion-layout">

          {/* Columna izquierda: métricas */}
          <div className="evaluacion-metricas-col">
            <div className="evaluacion-header-escala">
              <span className="evaluacion-header-titulo">PROMEDIO POR MÉTRICA</span>
              <span className="evaluacion-escala-detalle">(Escala: 1=Malo · 2=Regular · 3=Bueno · 4=Excelente)</span>
              <div className="evaluacion-periodo-selector">
                <Form.Control as="select" size="sm" value={mesSeleccionado}
                  onChange={(e) => setMesSeleccionado(e.target.value)}>
                  {getMesesDisponibles().map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Form.Control>
                <span className="evaluacion-total-badge">
                  {totalEncuestas} evaluación{totalEncuestas !== 1 ? 'es' : ''}
                </span>
              </div>
            </div>

            {cargando ? (
              <div className="bg-preload content-box"><div className="load-skeleton"></div></div>
            ) : sinDatos ? (
              <p className="evaluacion-sin-datos">
                No existen datos suficientes para generar el indicador en el período seleccionado.
              </p>
            ) : datos ? (
              <div className="evaluacion-metricas-row">
                {datos.map((item, idx) => {
                  const IconComponent = item.icon;
                  return (
                    <div key={idx} className="evaluacion-metrica-card">
                      <div className="evaluacion-metrica-header">
                        <div className="evaluacion-metrica-emoji">
                          <IconComponent style={{ fontSize: 32, color: '#1CBBC1' }} />
                        </div>
                        <div className="evaluacion-metrica-nombre">{item.nombre}</div>
                      </div>
                      <div className="evaluacion-metrica-body">
                        <div className="evaluacion-metrica-valor" style={{ color: getColor(item.promedio) }}>
                          {item.promedio > 0 ? item.promedio.toFixed(2) : '—'}
                        </div>
                        <div className="evaluacion-metrica-escala">/4.00</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          {/* Columna derecha: promedio general */}
          <div className="evaluacion-promedio-col">
            <div className="evaluacion-promedio-header">PROMEDIO GENERAL</div>
            <div className="evaluacion-promedio-contenido">
              {cargando ? (
                <div className="bg-preload" style={{ height: '80px' }}><div className="load-skeleton"></div></div>
              ) : (
                <div className="evaluacion-promedio-row">
                  <div className="evaluacion-promedio-texto">
                    <div className="evaluacion-promedio-valor" style={{ color: getColor(promedioGeneral) }}>
                      {promedioGeneral > 0 ? promedioGeneral.toFixed(2) : '—'}
                    </div>
                    <div className="evaluacion-promedio-escala">de 4.00</div>
                  </div>
                  <div className="evaluacion-promedio-dona">
                    <Doughnut
                      data={getDonaPromedioData()}
                      options={{
                        cutout: '70%',
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: { enabled: false }
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default EvaluacionProveedores;
